import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Crown,
  Star,
  CheckCircle,
  Loader2,
  Shield,
  CreditCard,
  Users,
  MessageSquare,
  Image as ImageIcon,
  TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContractorPremium() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');

  const handleCheckout = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const packageId = billingCycle === 'annual' ? 'premium_listing_annual' : 'premium_listing';
      const response = await axios.post(`${BACKEND_URL}/api/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
        customer_email: email,
        metadata: {
          tier: 'premium_listing',
          billing_cycle: billingCycle,
          source: 'contractor_upgrade'
        }
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                <Crown className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg">Premium Listing</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Benefits Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/10 border border-yellow-500/20 mb-6">
              <Star className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-yellow-400 font-medium">Stand Out from the Crowd</span>
            </div>
            
            <h1 className="text-4xl font-bold mb-4">
              Get More Customers with{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                Premium
              </span>
            </h1>
            
            <p className="text-slate-400 text-lg mb-8">
              Boost your visibility and credibility with premium placement in our contractor directory.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Featured Placement</h3>
                  <p className="text-sm text-slate-400">Your listing appears at the top of search results in your state</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                  <ImageIcon className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Logo & Branding</h3>
                  <p className="text-sm text-slate-400">Upload your company logo to stand out from basic listings</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Review Management</h3>
                  <p className="text-sm text-slate-400">Respond to customer reviews and build trust</p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                  <Crown className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Premium Badge</h3>
                  <p className="text-sm text-slate-400">Gold "Featured" badge signals quality to customers</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Checkout Card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="bg-slate-900 border-2 border-yellow-500/50 rounded-2xl p-8 sticky top-24">
              {/* Billing Toggle */}
              <div className="flex items-center justify-center gap-3 mb-6">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    billingCycle === 'monthly' 
                      ? 'bg-yellow-500/20 text-yellow-400' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    billingCycle === 'annual' 
                      ? 'bg-yellow-500/20 text-yellow-400' 
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Annual
                  <span className="ml-2 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                    Save $98
                  </span>
                </button>
              </div>
              
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/20 rounded-full text-yellow-400 text-sm font-medium mb-4">
                  <Crown className="w-4 h-4" />
                  Premium Listing
                </div>
                {billingCycle === 'monthly' ? (
                  <>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-bold">$49</span>
                      <span className="text-slate-400">/month</span>
                    </div>
                    <p className="text-slate-400 text-sm mt-2">Cancel anytime</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-5xl font-bold">$490</span>
                      <span className="text-slate-400">/year</span>
                    </div>
                    <p className="text-green-400 text-sm mt-2">
                      2 months FREE! ($41/mo effective)
                    </p>
                  </>
                )}
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-yellow-400" />
                  <span>Featured placement in directory</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-yellow-400" />
                  <span>Upload company logo</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-yellow-400" />
                  <span>Respond to reviews</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-yellow-400" />
                  <span>Premium "Featured" badge</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-yellow-400" />
                  <span>Performance analytics dashboard</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-yellow-400" />
                  <span>Priority customer support</span>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Your Email (same as registration)</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="bg-slate-800 border-slate-700"
                    data-testid="premium-email-input"
                  />
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-semibold py-6 text-lg"
                  data-testid="premium-checkout-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Crown className="w-5 h-5 mr-2" />
                      Subscribe Now
                    </>
                  )}
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="mt-6 flex justify-center gap-6 text-slate-500 text-sm">
                <div className="flex items-center gap-1">
                  <Shield className="w-4 h-4" />
                  <span>Secure</span>
                </div>
                <div className="flex items-center gap-1">
                  <CreditCard className="w-4 h-4" />
                  <span>Stripe</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-16"
        >
          <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h3 className="font-semibold mb-2">How quickly will I see results?</h3>
              <p className="text-slate-400 text-sm">
                Your premium features activate immediately after payment. Featured placement typically increases inquiries by 2-3x.
              </p>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h3 className="font-semibold mb-2">Can I cancel anytime?</h3>
              <p className="text-slate-400 text-sm">
                Yes! Cancel with one click anytime. You'll keep premium features until the end of your billing period.
              </p>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h3 className="font-semibold mb-2">Do I need AI automation too?</h3>
              <p className="text-slate-400 text-sm">
                No! Premium Listing is standalone. But combining it with our AI automation ($1,997 setup) gives you the best results.
              </p>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
              <h3 className="font-semibold mb-2">What if I'm not satisfied?</h3>
              <p className="text-slate-400 text-sm">
                Contact us within 30 days and we'll issue a full refund, no questions asked.
              </p>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
